import capitalize from "lodash/capitalize";
import { PostFormState } from "../../../../shared/types/post";
import { ContextMenu } from "./context-menu";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import SkedIcon from "../../../../ui/SkedIcon/SkedIcon";

export const PostMiniCardBase = ({
  card,
  onClick,
  onClose,
  onDuplicate,
  showDuplicate,
  isClustered,
}: {
  card: Card;
  onClick: () => void;
  onClose: () => void;
  onDuplicate: () => void;
  showDuplicate?: boolean;
  isClustered?: boolean;
}) => {
  const cardWidth = card.isSelected
    ? isClustered
      ? "w-[208px]"
      : "w-[216px]"
    : isClustered
      ? "w-[200px]"
      : "w-[208px]";

  return (
    <div
      className={`relative ${card.isSelected ? "-mr-[8px] h-[68px] py-[4px]" : "h-[55px]"}`}
      onClick={onClick}
    >
      <div
        data-testid="post-mini-card"
        className={`group relative flex rounded-l-[8px] bg-white ${cardWidth} ${card.isSelected ? "h-[60px] shadow-[0_2px_4px_-2px_rgba(16,24,40,0.06),0_4px_8px_-2px_rgba(16,24,40,0.1)]" : "h-[55px] rounded-r-[8px]"}`}
      >
        <CardPreview content={card.media} selected={!!card.isSelected} />
        <div className="flex flex-col flex-1 px-[8px] py-[4px]">
          <PostTypeInfo postType={card.postType} />

          <div className="flex items-center mt-auto">
            <PostStatus status={card.postStatus} />
            <FormState card={card} />
          </div>
          <ContextMenu
            isSelected={!!card.isSelected}
            onClose={onClose}
            onDuplicate={onDuplicate}
            allowDuplicate={!!showDuplicate}
          />
        </div>
      </div>
      <div
        className={`absolute bottom-0 right-[-8px] top-0 w-[8px] ${card.isSelected ? "bg-white" : "bg-transparent"}`}
      />
    </div>
  );
};
function PostStatus({ status }: { status: Card["postStatus"] | undefined }) {
  status = status || {
    color: "var(--color-neutral-300)",
    text: "Work in progress",
  };
  return (
    <div className="flex gap-[3px] items-center min-w-0">
      <div
        className="shrink-0 w-[10px] h-[10px] rounded-[2px]"
        style={{
          backgroundColor: status.color,
        }}
      />
      <span className="mr-[4px] truncate font-inter text-[10px] font-normal leading-[14px] text-neutral-800">
        {status.text}
      </span>
    </div>
  );
}

function PostTypeInfo({ postType }: { postType: CardPostType | undefined }) {
  if (postType?.type === "archived") {
    return (
      <div className="self-start flex items-center rounded-[16px] bg-neutral-25 px-[6px] py-[3px] font-inter text-[10px] font-normal leading-[14px] text-neutral-800">
        <i className="fa-regular fa-box-archive text-[12px] pr-[4px]" />
        Archived
      </div>
    );
  }
  if (postType?.type === "queued") {
    return (
      <div className="self-start flex items-center rounded-[16px] bg-neutral-25 px-[6px] py-[3px] font-inter text-[10px] font-normal leading-[14px] text-blue-500">
        <i className="fa-regular fa-layer-group text-[12px] pr-[4px]" />
        Queue
      </div>
    );
  }
  if (!postType || postType.type === "draft") {
    return (
      <div className="self-start flex items-center rounded-[16px] bg-neutral-25 px-[6px] py-[3px] font-inter text-[10px] font-normal leading-[14px] text-neutral-800">
        <i className="fa-regular fa-file text-[12px] pr-[4px]" />
        {capitalize(postType?.type || "draft")}
      </div>
    );
  }
  if (postType.type === "scheduled") {
    if (postType.isNow) {
      return (
        <div className="self-start flex items-center rounded-[16px] bg-blue-25 px-[6px] py-[3px] font-inter text-[10px] font-normal leading-[14px] text-blue-500">
          <i className="fa-regular fa-paper-plane-top text-[12px] pr-[4px]" />
          ASAP
        </div>
      );
    }
    return (
      <div className="self-start flex items-center rounded-[16px] bg-blue-25 px-[6px] py-[3px] font-inter text-[10px] font-normal leading-[14px] text-blue-500">
        <i className="fa-regular fa-alarm-clock text-[12px] pr-[4px]" />
        {postType.isNow ? "Post ASAP" : postType.scheduledTime}
      </div>
    );
  }
  return null;
}

function FormState({ card }: { card: Card }) {
  return (
    <div className="flex ml-auto shrink-0 gap-[2px]">
      {(() => {
        const icons: React.ReactNode[] = [];

        if (card.errorCount) {
          icons.push(
            <Tooltip key="error-tooltip">
              <TooltipTrigger asChild>
                <div className="flex w-[10px] h-[10px]  bg-red-500 justify-center items-center rounded-[2px]">
                  <i
                    key="error"
                    className="fa-solid fa-xmark text-[6px] text-white"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs">
                {`${card.errorCount} error${card.errorCount === 1 ? "" : "s"}`}
              </TooltipContent>
            </Tooltip>
          );
        }
        if (card.warningCount) {
          icons.push(
            <Tooltip key="warning-tooltip">
              <TooltipTrigger asChild>
                <div className="flex w-[10px] h-[10px]  bg-yellow-500 justify-center items-center rounded-[2px]">
                  <i
                    key="warning"
                    className="fa-solid fa-exclamation text-[7px] text-white"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs">
                {`${card.warningCount} warning${card.warningCount === 1 ? "" : "s"}`}
              </TooltipContent>
            </Tooltip>
          );
        }
        if (card.editState === "editing") {
          icons.push(
            <Tooltip key="editing-tooltip">
              <TooltipTrigger asChild>
                <div className="flex w-[10px] h-[10px]  bg-blue-500 justify-center items-center rounded-[2px]">
                  <i
                    key="editing"
                    className="fa-solid fa-pencil text-[6px] text-white"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Unsaved</TooltipContent>
            </Tooltip>
          );
        } else {
          icons.push(
            <Tooltip key="saved-tooltip">
              <TooltipTrigger asChild>
                <div className="flex w-[10px] h-[10px]  bg-green-500 justify-center items-center rounded-[2px]">
                  <i
                    key="saved"
                    className="fa-solid fa-check text-[6px] text-white"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Saved</TooltipContent>
            </Tooltip>
          );
        }

        return icons;
      })()}
    </div>
  );
}

function CardPreview({
  content,
  selected,
}: {
  content: CardMedia;
  selected: boolean;
}) {
  const previewSize = selected ? "h-[60px] w-[48px]" : "h-[55px] w-[44px]";
  const renderImgSection = (src: string, icon: React.ReactNode) => {
    return (
      <div
        className={`relative flex-shrink-0 rounded-l-[8px] bg-neutral-300 ${previewSize}`}
      >
        <img
          src={src}
          alt=""
          className="object-cover w-full h-full rounded-l-[8px]"
        />
        <div className="flex absolute size-[20px] top-[4px] right-[4px] bg-black/40 rounded-full items-center justify-center">
          {icon}
        </div>
      </div>
    );
  };
  const emptyArea = (
    <div
      className={`flex flex-shrink-0 items-center justify-center rounded-l-[8px] bg-neutral-800 p-[4px] ${previewSize}`}
    >
      <i className="fa-regular fa-image-slash text-white text-[16px]" />
    </div>
  );
  const skedIcon = (icon: string) => {
    return (
      <SkedIcon
        icon={icon}
        size="12"
        color="white"
        className="cursor-default"
      />
    );
  };
  switch (content.type) {
    case "text": {
      if (!content.text?.trim()) {
        return emptyArea;
      }

      return (
        <div className="flex-shrink-0  w-[44px] h-[55px] p-[4px] text-white bg-blue-300 rounded-l-[8px]">
          <p className="text-[8px] line-clamp-4 leading-[10px] wrap-break-word text-white">
            {content.text}
          </p>
        </div>
      );
    }
    case "image": {
      return renderImgSection(
        content.thumbnail,
        <i className="fa-regular fa-image text-white text-[12px]" />
      );
    }
    case "reel": {
      return renderImgSection(content.thumbnail, skedIcon("icon-post-reel"));
    }
    case "video": {
      return renderImgSection(
        content.thumbnail,
        <i className="fa-regular fa-video text-white text-[12px]" />
      );
    }
    case "carousel": {
      return renderImgSection(
        content.thumbnail,
        <i className="fa-regular fa-rectangle-vertical-history text-white text-[12px]" />
      );
    }
    case "document": {
      return renderImgSection(
        content.thumbnail,
        <i className="fa-regular fa-file-pdf text-white text-[12px]" />
      );
    }
    case "unsupported": {
      return emptyArea;
    }
    case "story": {
      if (!content.thumbnail) {
        return emptyArea;
      }

      return renderImgSection(content.thumbnail, skedIcon("icon-post-story"));
    }
    default: {
      return (
        <div className="flex-shrink-0 w-[44px] h-[55px] p-[4px] bg-neutral-300 rounded-l-[8px]">
          <p className="text-[8px] line-clamp-4 leading-[10px] wrap-break-word">
            Unsupported content
          </p>
        </div>
      );
    }
  }
}

interface TextCardMedia {
  type: "text";
  text: string;
}

interface CommonCardMedia {
  type: "video" | "image" | "carousel" | "document" | "reel" | "igtv" | "story";
  thumbnail: string;
}

interface UnsupportedCardMedia {
  type: "unsupported";
}

type CardMedia = TextCardMedia | CommonCardMedia | UnsupportedCardMedia;

interface DraftPostType {
  type: "draft";
}
interface QueuePostType {
  type: "queued";
}
interface ArchivedPostType {
  type: "archived";
}

interface ScheduledPostType {
  type: "scheduled";
  scheduledTime: string;
  isNow: boolean;
}

type CardPostType =
  DraftPostType | ScheduledPostType | QueuePostType | ArchivedPostType;

export interface Card {
  postId: string;
  postType?: CardPostType;
  media: CardMedia;
  editState?: "saved" | "editing";
  errorCount?: number;
  warningCount?: number;
  postStatus?: {
    color: string;
    text: string;
  };
  isSelected?: boolean;
  clusterId?: string;
  form: PostFormState;
}
